import React, { useMemo, useState } from 'react';
import { Monitor, Smartphone } from 'lucide-react';
import Boutique from './templates/Boutique';
import Market from './templates/Market';
import Studio from './templates/Studio';
import { DEMO_CONFIGS, TEMPLATE_META } from './demo';
import { contrastOnWhite, type StorefrontConfig, type TemplateId } from './types';

const TEMPLATES: Record<TemplateId, React.FC<{ config: StorefrontConfig }>> = {
  boutique: Boutique,
  market: Market,
  studio: Studio,
};

const ACCENT_PRESETS = ['#16191D', '#1097C1', '#209347', '#8A6F52', '#9D5FDB', '#D70E0E'];

/**
 * Template gallery and live preview.
 *
 * Its real job is to prove the contract: every template renders from a single
 * `StorefrontConfig` and nothing else, so editing the accent, name, bio or
 * price visibility here changes the store immediately. When the business
 * app's storefront settings are eventually wired to a backend, that's the
 * only thing that changes — the same object arrives from an API instead of
 * this panel.
 *
 * Nothing here talks to an API on purpose. The business app's backend shape
 * is still being decided, and binding now would bake in a guess.
 */
export default function StorefrontPreview() {
  const [templateId, setTemplateId] = useState<TemplateId>('boutique');
  const [overrides, setOverrides] = useState<Partial<StorefrontConfig>>({});
  const [wide, setWide] = useState(true);

  // Overrides reset per template so switching doesn't carry a fashion accent
  // onto the grocery store and make both look wrong.
  const config = useMemo<StorefrontConfig>(
    () => ({ ...DEMO_CONFIGS[templateId], ...overrides }),
    [templateId, overrides],
  );

  const Template = TEMPLATES[templateId];
  const accentContrast = contrastOnWhite(config.accent);

  function selectTemplate(id: TemplateId) {
    setTemplateId(id);
    setOverrides({});
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minHeight: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ padding: 'var(--space-6)', display: 'grid', gap: 'var(--space-5)' }}>
        <header style={{ display: 'grid', gap: 6 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Storefront templates</h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 720 }}>
            One template per core category. Every template renders from a single config object, so
            changing settings below updates the store live — that's the same object a merchant's
            saved settings will supply once the backend is decided.
          </p>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {TEMPLATE_META.map((t) => {
            const active = t.id === templateId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => selectTemplate(t.id)}
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  background: 'var(--bg-card)',
                  border: active ? '2px solid var(--text-primary)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-4)',
                  display: 'grid',
                  gap: 6,
                }}
              >
                <strong style={{ fontSize: 15 }}>{t.name}</strong>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.category}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {t.rationale}
                </span>
              </button>
            );
          })}
        </div>

        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-4)',
            alignItems: 'flex-end',
          }}
        >
          <Field label="Shop name">
            <input
              value={config.name}
              onChange={(e) => setOverrides((o) => ({ ...o, name: e.target.value }))}
              style={inputStyle}
            />
          </Field>

          <Field label="Bio">
            <input
              value={config.bio}
              onChange={(e) => setOverrides((o) => ({ ...o, bio: e.target.value }))}
              style={{ ...inputStyle, minWidth: 280 }}
            />
          </Field>

          <Field label="Accent">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {ACCENT_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Accent ${c}`}
                  onClick={() => setOverrides((o) => ({ ...o, accent: c }))}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    cursor: 'pointer',
                    background: c,
                    border: config.accent === c ? '2px solid var(--text-primary)' : '1px solid var(--border)',
                  }}
                />
              ))}
              <input
                type="color"
                value={config.accent}
                onChange={(e) => setOverrides((o) => ({ ...o, accent: e.target.value.toUpperCase() }))}
                style={{ width: 34, height: 30, border: '1px solid var(--border)', borderRadius: 6, background: 'none' }}
              />
            </div>
          </Field>

          <Field label="Prices">
            <button
              type="button"
              onClick={() => setOverrides((o) => ({ ...o, showPrices: !config.showPrices }))}
              style={{ ...pillStyle, background: config.showPrices ? 'var(--text-primary)' : 'var(--bg-muted)', color: config.showPrices ? '#FFF' : 'var(--text-secondary)' }}
            >
              {config.showPrices ? 'Shown' : 'Hidden'}
            </button>
          </Field>

          <Field label="Width">
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setWide(true)} style={{ ...pillStyle, background: wide ? 'var(--text-primary)' : 'var(--bg-muted)', color: wide ? '#FFF' : 'var(--text-secondary)' }}>
                <Monitor size={14} />
              </button>
              <button type="button" onClick={() => setWide(false)} style={{ ...pillStyle, background: !wide ? 'var(--text-primary)' : 'var(--bg-muted)', color: !wide ? '#FFF' : 'var(--text-secondary)' }}>
                <Smartphone size={14} />
              </button>
            </div>
          </Field>
        </div>

        {/* Same guard the business app's picker applies. Templates already
            flip their own label colour, but an accent this pale still reads
            as washed out against white page furniture. */}
        {accentContrast < 3 ? (
          <div
            style={{
              background: 'var(--yellow-100)',
              color: 'var(--yellow-900)',
              border: '1px solid var(--yellow-200)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              fontSize: 13,
            }}
          >
            This accent scores {accentContrast.toFixed(1)}:1 against white. Templates will switch to dark
            label text automatically, but it will still look washed out on light sections.
          </div>
        ) : null}

        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            background: '#FFFFFF',
            width: wide ? '100%' : 420,
            maxWidth: '100%',
            margin: wide ? undefined : '0 auto',
            // `container-type` would be the modern answer, but the templates
            // use viewport units for their display type, so an iframe-free
            // narrow preview is an approximation of phone width, not a
            // simulation of it.
            resize: 'vertical',
          }}
        >
          <Template config={config} />
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  height: 38,
  minWidth: 180,
  padding: '0 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--text-primary)',
  background: 'var(--bg-card)',
};

const pillStyle: React.CSSProperties = {
  height: 38,
  padding: '0 16px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{label}</span>
      {children}
    </label>
  );
}
